'use client';
import React, { useState } from 'react';
import AppLayout from '@/components/AppLayout';
import Link from 'next/link';
import { DEVELOPMENTAL_LEVELS, getFeaturedSubjects, type DevelopmentalLevel } from '@/lib/curriculum-data';

const LANGUAGES = [
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'ar', label: 'العربية', flag: '🇸🇦' },
  { code: 'fr', label: 'Français', flag: '🇫🇷' },
  { code: 'ha', label: 'Hausa', flag: '🇳🇬' },
  { code: 'sw', label: 'Swahili', flag: '🇰🇪' },
  { code: 'ur', label: 'اردو', flag: '🇵🇰' },
  { code: 'es', label: 'Español', flag: '🇪🇸' },
  { code: 'pt', label: 'Português', flag: '🇧🇷' },
  { code: 'yo', label: 'Yorùbá', flag: '🇳🇬' },
];

const QUICK_STATS = [
  { label: 'Lessons Completed', value: '47', icon: '📚', color: 'bg-blue-50 border-blue-200 text-blue-700' },
  { label: 'Current Streak', value: '21 days', icon: '🔥', color: 'bg-orange-50 border-orange-200 text-orange-700' },
  { label: 'XP Earned', value: '4,820', icon: '⭐', color: 'bg-amber-50 border-amber-200 text-amber-700' },
  { label: 'Certificates', value: '3', icon: '🏆', color: 'bg-emerald-50 border-emerald-200 text-emerald-700' },
];

const TODAY_LESSONS = [
  { id: 1, subject: 'Quran & Tajweed', title: 'Surah Al-Mulk: Verses 1–10', duration: '30 min', type: 'Memorization', emoji: '📿', progress: 0, level: 'Foundation' },
  { id: 2, subject: 'Mathematics', title: 'Algebra: Solving Linear Equations', duration: '40 min', type: 'Video + Quiz', emoji: '🔢', progress: 60, level: 'High School' },
  { id: 3, subject: 'Biology', title: 'Cell Division: Mitosis & Meiosis', duration: '45 min', type: 'Interactive Lab', emoji: '🧬', progress: 0, level: 'Middle School' },
  { id: 4, subject: 'Arabic', title: 'Verb Conjugation: Past Tense', duration: '25 min', type: 'Practice', emoji: '🔤', progress: 100, level: 'Elementary' },
];

const RECOMMENDED = [
  { id: 1, title: 'Python for Beginners', subject: 'Computer Science', emoji: '💻', duration: '50 min', rating: 4.9, enrolled: 1240 },
  { id: 2, title: 'Islamic Finance Basics', subject: 'Islamic Finance', emoji: '💰', duration: '35 min', rating: 4.8, enrolled: 890 },
  { id: 3, title: 'Human Anatomy: Skeletal System', subject: 'Biology', emoji: '🦴', duration: '40 min', rating: 4.7, enrolled: 2100 },
];

export default function StudentDashboard() {
  const [selectedLevel, setSelectedLevel] = useState<DevelopmentalLevel>('high_school');
  const [selectedLang, setSelectedLang] = useState('en');
  const featuredSubjects = getFeaturedSubjects();

  return (
    <AppLayout activeRoute="/student-dashboard">
      <div className="space-y-8 animate-fade-in">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-800 text-foreground">السلام عليكم، Yusuf! 👋</h1>
            <p className="text-sm text-muted-foreground mt-1">Wednesday, 30 April 2026 · Day 14 of Ramadan Term · High School Level</p>
          </div>
          <div className="flex items-center gap-3">
            {/* Language Selector */}
            <select
              value={selectedLang}
              onChange={e => setSelectedLang(e.target.value)}
              className="text-xs border border-border rounded-xl px-3 py-2 bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
            >
              {LANGUAGES.map(l => (
                <option key={l.code} value={l.code}>{l.flag} {l.label}</option>
              ))}
            </select>
            <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2">
              <span className="text-lg">🔥</span>
              <div>
                <p className="text-sm font-700 text-amber-700">21 days</p>
                <p className="text-xs text-amber-600">Streak</p>
              </div>
            </div>
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center text-white font-700">Y</div>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {QUICK_STATS.map((stat, i) => (
            <div key={i} className={`rounded-2xl border p-4 ${stat.color}`}>
              <div className="text-2xl mb-1">{stat.icon}</div>
              <p className="text-xl font-800">{stat.value}</p>
              <p className="text-xs font-500 mt-0.5 opacity-80">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Level Selector */}
        <div>
          <p className="text-xs font-700 uppercase tracking-widest text-muted-foreground mb-3">My Learning Level</p>
          <div className="flex flex-wrap gap-2">
            {(Object.entries(DEVELOPMENTAL_LEVELS) as [DevelopmentalLevel, any][]).map(([key, info]) => (
              <button
                key={key}
                onClick={() => setSelectedLevel(key)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-600 border transition-all ${selectedLevel === key ? 'bg-primary text-white border-primary shadow-sm' : 'bg-card border-border text-foreground hover:border-primary/40'}`}
              >
                <span>{info.emoji}</span>
                <span className="hidden sm:inline">{info.label}</span>
                <span className="text-[10px] opacity-70 hidden md:inline">({info.ageRange})</span>
              </button>
            ))}
          </div>
        </div>

        {/* Main Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Today's Lessons */}
          <div className="lg:col-span-2 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="font-800 text-foreground">Today's Lessons</h2>
              <Link href="/lesson-player" className="text-xs text-primary font-600 hover:underline">View all →</Link>
            </div>
            <div className="space-y-3">
              {TODAY_LESSONS.map(lesson => (
                <div key={lesson.id} className="bg-card rounded-2xl border border-border p-4 hover:shadow-md transition-all group">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary/10 to-primary/20 flex items-center justify-center text-2xl flex-shrink-0">
                      {lesson.emoji}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="text-[10px] font-600 text-muted-foreground uppercase tracking-wide">{lesson.subject}</span>
                        <span className="text-[10px] bg-muted text-muted-foreground rounded-full px-2 py-0.5">{lesson.level}</span>
                      </div>
                      <p className="font-700 text-sm text-foreground truncate">{lesson.title}</p>
                      <div className="flex items-center gap-3 mt-1">
                        <span className="text-xs text-muted-foreground">{lesson.duration}</span>
                        <span className="text-xs text-muted-foreground">·</span>
                        <span className="text-xs text-muted-foreground">{lesson.type}</span>
                      </div>
                      {lesson.progress > 0 && (
                        <div className="mt-2">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-[10px] text-muted-foreground">Progress</span>
                            <span className="text-[10px] font-600 text-primary">{lesson.progress}%</span>
                          </div>
                          <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                            <div className="h-full bg-primary rounded-full transition-all" style={{ width: `${lesson.progress}%` }} />
                          </div>
                        </div>
                      )}
                    </div>
                    <Link
                      href="/lesson-player"
                      className={`flex-shrink-0 px-4 py-2 rounded-xl text-xs font-700 transition-all ${lesson.progress === 100 ? 'bg-emerald-100 text-emerald-700' : lesson.progress > 0 ? 'bg-primary text-white hover:bg-primary/90' : 'bg-primary/10 text-primary hover:bg-primary hover:text-white'}`}
                    >
                      {lesson.progress === 100 ? '✓ Done' : lesson.progress > 0 ? 'Continue' : 'Start'}
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right Column */}
          <div className="space-y-6">
            {/* Hifdh Progress */}
            <div className="bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl p-5 text-white">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-2xl">📿</span>
                <div>
                  <p className="font-800 text-sm">Hifdh Progress</p>
                  <p className="text-xs opacity-80">Juz 30 — Juz Amma</p>
                </div>
              </div>
              <div className="space-y-2">
                {[
                  { surah: 'Al-Fatiha', status: 'complete' },
                  { surah: 'Al-Nas to Al-Duha', status: 'complete' },
                  { surah: 'Al-Layl to Al-Ghashiyah', status: 'in-progress' },
                  { surah: 'Al-Ala to Al-Naba', status: 'locked' },
                ].map((item, i) => (
                  <div key={i} className="flex items-center justify-between">
                    <span className="text-xs opacity-90">{item.surah}</span>
                    <span className="text-xs">
                      {item.status === 'complete' ? '✅' : item.status === 'in-progress' ? '🔄' : '🔒'}
                    </span>
                  </div>
                ))}
              </div>
              <Link href="/islamic-education" className="mt-3 block text-center text-xs font-700 bg-white/20 hover:bg-white/30 rounded-xl py-2 transition-colors">
                Continue Hifdh →
              </Link>
            </div>

            {/* Recommended */}
            <div>
              <h3 className="font-800 text-foreground mb-3">Recommended for You</h3>
              <div className="space-y-3">
                {RECOMMENDED.map(item => (
                  <div key={item.id} className="bg-card rounded-xl border border-border p-3 hover:shadow-sm transition-all">
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{item.emoji}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-700 text-foreground truncate">{item.title}</p>
                        <p className="text-[10px] text-muted-foreground">{item.subject} · {item.duration}</p>
                        <div className="flex items-center gap-1 mt-0.5">
                          <span className="text-[10px] text-amber-500">★</span>
                          <span className="text-[10px] font-600 text-foreground">{item.rating}</span>
                          <span className="text-[10px] text-muted-foreground">({item.enrolled.toLocaleString()})</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Featured Subjects */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-800 text-foreground">Explore Subjects</h2>
            <Link href="/subject-hub" className="text-xs text-primary font-600 hover:underline">All subjects →</Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {featuredSubjects.map(subject => (
              <Link
                key={subject.id}
                href={`/subject/${subject.id}`}
                className="bg-card rounded-2xl border border-border p-4 hover:shadow-md transition-all group text-center"
              >
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${subject.gradient} flex items-center justify-center text-2xl mx-auto mb-3 shadow-sm group-hover:scale-110 transition-transform`}>
                  {subject.emoji}
                </div>
                <p className="text-xs font-700 text-foreground">{subject.name}</p>
                {subject.nameAr && <p className="text-[10px] text-muted-foreground mt-0.5">{subject.nameAr}</p>}
                <p className="text-[10px] text-muted-foreground mt-1">{subject.totalLessons} lessons</p>
              </Link>
            ))}
          </div>
        </div>

        {/* Offline Mode Banner */}
        <div className="bg-gradient-to-r from-slate-800 to-slate-900 rounded-2xl p-5 text-white flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <span className="text-3xl">📥</span>
            <div>
              <p className="font-800">Offline Learning Available</p>
              <p className="text-sm opacity-80 mt-0.5">Download lessons for internet-free learning on tablets, phones, and community hubs.</p>
            </div>
          </div>
          <button className="flex-shrink-0 bg-white text-slate-900 font-700 text-sm px-4 py-2 rounded-xl hover:bg-white/90 transition-colors">
            Download
          </button>
        </div>
      </div>
    </AppLayout>
  );
}
